import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Client, ClientFormData, formDataToClient, clientToFormData } from "@/types/contacts";
import { toast } from "sonner";
import { useCallback, useMemo, useState, useEffect } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import {
  searchClients,
  ClientSearchOptions,
  ClientSearchResult,
  parseAdvancedSearchSyntax,
  getCodeSuggestions,
  clearClientSearchCache,
  getSearchMetrics,
  resetSearchMetrics
} from "@/utils/clientSearch";

const CLIENTS_QUERY_KEY = ["clients"];

// Função para buscar todos os clientes
async function fetchClients(): Promise<Client[]> {
  const { data, error } = await supabase
    .from("clients")
    .select("*")
    .order("name", { ascending: true });

  if (error) {
    console.error("Error fetching clients:", error);
    throw error;
  }

  console.log('🔍 [FETCH] Dados brutos do Supabase:', data);

  // Transformar dados do banco (snake_case) para o formato Client (camelCase)
  const transformedClients = (data || []).map(dbClient => ({
    id: dbClient.id,
    userId: dbClient.user_id,
    name: dbClient.name,
    documentType: dbClient.document_type,
    documentNumber: dbClient.document_number,
    registrationCode: dbClient.registration_code,
    email: dbClient.email,
    phone: dbClient.phone,
    address: {
      street: dbClient.address_street,
      number: dbClient.address_number,
      complement: dbClient.address_complement,
      neighborhood: dbClient.address_neighborhood,
      city: dbClient.address_city,
      state: dbClient.address_state,
      zipCode: dbClient.address_zip_code,
    },
    bankInfo: {
      bankName: dbClient.bank_name,
      agency: dbClient.bank_agency,
      account: dbClient.bank_account,
      accountType: dbClient.bank_account_type,
    },
    paymentTerms: dbClient.payment_terms,
    notes: dbClient.notes,
    isActive: dbClient.is_active,
    createdAt: dbClient.created_at,
    updatedAt: dbClient.updated_at,
  }));

  console.log('✨ [FETCH] Dados transformados:', transformedClients);

  return transformedClients;
}

// Função para buscar um cliente pelo ID
async function fetchClientById(id: string): Promise<Client> {
  const { data, error } = await supabase
    .from("clients")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    console.error("Error fetching client:", error);
    throw error;
  }

  // Transformar dados do banco (snake_case) para o formato Client (camelCase)
  return {
    id: data.id,
    userId: data.user_id,
    name: data.name,
    documentType: data.document_type,
    documentNumber: data.document_number,
    registrationCode: data.registration_code,
    email: data.email,
    phone: data.phone,
    address: {
      street: data.address_street,
      number: data.address_number,
      complement: data.address_complement,
      neighborhood: data.address_neighborhood,
      city: data.address_city,
      state: data.address_state,
      zipCode: data.address_zip_code,
    },
    bankInfo: {
      bankName: data.bank_name,
      agency: data.bank_agency,
      account: data.bank_account,
      accountType: data.bank_account_type,
    },
    paymentTerms: data.payment_terms,
    notes: data.notes,
    isActive: data.is_active,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

function normalizeBankAccountType(
  type?: string | null
): 'corrente' | 'poupanca' | null {
  if (!type) return null;
  const normalized = type.trim().toLowerCase();
  return normalized === 'corrente' || normalized === 'poupanca' ? normalized : null;
}

// Função para criar um novo cliente
async function createClient(formData: ClientFormData, userId: string): Promise<Client> {
  console.log('📝 [CREATE] FormData recebido:', formData);
  const clientData = formDataToClient(formData, userId);
  console.log('🔄 [CREATE] ClientData transformado:', clientData);

  // Transformar o objeto aninhado para o formato do banco de dados
  const dbData = {
    user_id: clientData.userId,
    name: clientData.name,
    document_type: clientData.documentType || null,
    document_number: clientData.documentNumber || null,
    registration_code: clientData.registrationCode,
    email: clientData.email,
    phone: clientData.phone,
    address_street: clientData.address?.street,
    address_number: clientData.address?.number,
    address_complement: clientData.address?.complement,
    address_neighborhood: clientData.address?.neighborhood,
    address_city: clientData.address?.city,
    address_state: clientData.address?.state,
    address_zip_code: clientData.address?.zipCode,
    bank_name: clientData.bankInfo?.bankName,
    bank_agency: clientData.bankInfo?.agency,
    bank_account: clientData.bankInfo?.account,
    bank_account_type: normalizeBankAccountType(clientData.bankInfo?.accountType),
    payment_terms: clientData.paymentTerms,
    notes: clientData.notes,
    is_active: clientData.isActive,
  };

  console.log('💾 [CREATE] DbData para Supabase:', dbData);

  const { data, error } = await supabase
    .from("clients")
    .insert([dbData])
    .select()
    .single();

  console.log('✅ [CREATE] Resposta Supabase:', { data, error });

  if (error) {
    console.error("Error creating client:", error);
    throw error;
  }

  // Transformar de volta para o formato Client
  return {
    id: data.id,
    userId: data.user_id,
    name: data.name,
    documentType: data.document_type,
    documentNumber: data.document_number,
    registrationCode: data.registration_code,
    email: data.email,
    phone: data.phone,
    address: {
      street: data.address_street,
      number: data.address_number,
      complement: data.address_complement,
      neighborhood: data.address_neighborhood,
      city: data.address_city,
      state: data.address_state,
      zipCode: data.address_zip_code,
    },
    bankInfo: {
      bankName: data.bank_name,
      agency: data.bank_agency,
      account: data.bank_account,
      accountType: data.bank_account_type,
    },
    paymentTerms: data.payment_terms,
    notes: data.notes,
    isActive: data.is_active,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

// Função para atualizar um cliente
async function updateClient(id: string, formData: ClientFormData): Promise<Client> {
  console.log('📝 [UPDATE] FormData recebido:', formData);
  const clientData = formDataToClient(formData, ""); // userId não é necessário para update
  console.log('🔄 [UPDATE] ClientData transformado:', clientData);

  // Transformar o objeto aninhado para o formato do banco de dados
  const dbData = {
    name: clientData.name,
    document_type: clientData.documentType || null,
    document_number: clientData.documentNumber || null,
    registration_code: clientData.registrationCode,
    email: clientData.email,
    phone: clientData.phone,
    address_street: clientData.address?.street,
    address_number: clientData.address?.number,
    address_complement: clientData.address?.complement,
    address_neighborhood: clientData.address?.neighborhood,
    address_city: clientData.address?.city,
    address_state: clientData.address?.state,
    address_zip_code: clientData.address?.zipCode,
    bank_name: clientData.bankInfo?.bankName,
    bank_agency: clientData.bankInfo?.agency,
    bank_account: clientData.bankInfo?.account,
    bank_account_type: normalizeBankAccountType(clientData.bankInfo?.accountType),
    payment_terms: clientData.paymentTerms,
    notes: clientData.notes,
    is_active: clientData.isActive,
  };

  console.log('💾 [UPDATE] DbData para Supabase:', dbData);

  const { data, error } = await supabase
    .from("clients")
    .update(dbData)
    .eq("id", id)
    .select()
    .single();

  console.log('✅ [UPDATE] Resposta Supabase:', { data, error });

  if (error) {
    console.error("Error updating client:", error);
    throw error;
  }

  // Transformar de volta para o formato Client
  return {
    id: data.id,
    userId: data.user_id,
    name: data.name,
    documentType: data.document_type,
    documentNumber: data.document_number,
    registrationCode: data.registration_code,
    email: data.email,
    phone: data.phone,
    address: {
      street: data.address_street,
      number: data.address_number,
      complement: data.address_complement,
      neighborhood: data.address_neighborhood,
      city: data.address_city,
      state: data.address_state,
      zipCode: data.address_zip_code,
    },
    bankInfo: {
      bankName: data.bank_name,
      agency: data.bank_agency,
      account: data.bank_account,
      accountType: data.bank_account_type,
    },
    paymentTerms: data.payment_terms,
    notes: data.notes,
    isActive: data.is_active,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

// Função para deletar um cliente
async function deleteClient(id: string): Promise<void> {
  const { error } = await supabase
    .from("clients")
    .delete()
    .eq("id", id);

  if (error) {
    console.error("Error deleting client:", error);
    throw error;
  }
}

// Hook personalizado para gerenciar clientes
export function useClients(searchOptions?: Partial<ClientSearchOptions>) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [searchResult, setSearchResult] = useState<ClientSearchResult | null>(null);

  // Query para buscar todos os clientes
  const clientsQuery = useQuery({
    queryKey: CLIENTS_QUERY_KEY,
    queryFn: fetchClients,
    staleTime: 0, // Immediate refresh for better UX
  });

  // Debounce search query with 300ms delay
  const debouncedQuery = useDebounce(searchQuery, 300);

  // Update debounced search query when debounced value changes
  useEffect(() => {
    setDebouncedSearchQuery(debouncedQuery);
  }, [debouncedQuery]);

  // Enhanced search with useMemo for performance optimization
  const filteredClients = useMemo(() => {
    const allClients = clientsQuery.data || [];

    if (!debouncedSearchQuery.trim() && !searchOptions) {
      return allClients;
    }

    const options: ClientSearchOptions = {
      query: debouncedSearchQuery || '',
      includeInactive: false,
      searchFields: ['name', 'documentNumber', 'email', 'registrationCode', 'phone', 'city'],
      ...searchOptions
    };

    // Parse advanced search syntax
    const parsedOptions = debouncedSearchQuery.trim()
      ? parseAdvancedSearchSyntax(debouncedSearchQuery)
      : options;

    setIsSearchLoading(true);
    const result = searchClients(allClients, parsedOptions);
    setIsSearchLoading(false);
    setSearchResult(result);

    return result.clients;
  }, [clientsQuery.data, debouncedSearchQuery, searchOptions]);

  // Get code suggestions for autocomplete
  const getCodeSuggestionsCallback = useCallback((partialCode: string) => {
    if (!clientsQuery.data) return [];
    return getCodeSuggestions(clientsQuery.data, partialCode);
  }, [clientsQuery.data]);

  // Search function with debouncing
  const searchClientsCallback = useCallback((query: string, options?: Partial<ClientSearchOptions>) => {
    setSearchQuery(query);
    if (options) {
      // Merge with existing options if provided
      Object.assign(searchOptions || {}, options);
    }
  }, [searchOptions]);

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setSearchResult(null);
  }, []);

  // Query para buscar um cliente específico
  const useClient = (id: string) => {
    return useQuery({
      queryKey: [...CLIENTS_QUERY_KEY, id],
      queryFn: () => fetchClientById(id),
      enabled: !!id,
    });
  };

  // Mutation para criar cliente
  const createClientMutation = useMutation({
    mutationFn: ({ formData, userId }: { formData: ClientFormData; userId: string }) =>
      createClient(formData, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENTS_QUERY_KEY });
      queryClient.refetchQueries({ queryKey: CLIENTS_QUERY_KEY });
      // Clear search cache to include new client
      clearClientSearchCache();
      toast.success("Cliente criado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Error creating client:", error);

      // Exibir mensagem específica se for erro personalizado
      if (error.message && error.message.includes('CPF/CNPJ')) {
        toast.error(error.message);
      } else {
        toast.error("Erro ao criar cliente. Tente novamente.");
      }
    },
  });

  // Mutation para atualizar cliente
  const updateClientMutation = useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: ClientFormData }) =>
      updateClient(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENTS_QUERY_KEY });
      queryClient.refetchQueries({ queryKey: CLIENTS_QUERY_KEY });
      // Clear search cache to include updated client
      clearClientSearchCache();
      toast.success("Cliente atualizado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Error updating client:", error);

      // Exibir mensagem específica se for erro personalizado
      if (error.message && error.message.includes('CPF/CNPJ')) {
        toast.error(error.message);
      } else {
        toast.error("Erro ao atualizar cliente. Tente novamente.");
      }
    },
  });

  // Mutation para deletar cliente
  const deleteClientMutation = useMutation({
    mutationFn: deleteClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENTS_QUERY_KEY });
      queryClient.refetchQueries({ queryKey: CLIENTS_QUERY_KEY });
      // Clear search cache to remove deleted client
      clearClientSearchCache();
      toast.success("Cliente deletado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Error deleting client:", error);
      toast.error("Erro ao deletar cliente. Tente novamente.");
    },
  });

  return {
    // Queries
    clients: clientsQuery.data || [],
    filteredClients,
    isLoading: clientsQuery.isLoading,
    isSearchLoading,
    error: clientsQuery.error,
    searchResult,
    useClient,

    // Search functionality
    searchQuery,
    debouncedSearchQuery,
    setSearchQuery,
    searchClients: searchClientsCallback,
    clearSearch,
    getCodeSuggestions: getCodeSuggestionsCallback,

    // Mutations
    createClient: createClientMutation.mutateAsync,
    updateClient: updateClientMutation.mutateAsync,
    deleteClient: deleteClientMutation.mutateAsync,

    // Loading states
    isCreating: createClientMutation.isPending,
    isUpdating: updateClientMutation.isPending,
    isDeleting: deleteClientMutation.isPending,

    // Utility functions
    clearCache: clearClientSearchCache,
    getSearchMetrics,
    resetSearchMetrics,
  };
}
